import { selMessage } from "@selector/shared";
import { DB_FIELD_MAX_LENGTH } from "@selector/shared/settings";
import { clsx } from "clsx";
import { type FC, type ReactElement, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { FormatOptionLabelMeta } from "react-select";
import type { FilterOptionOption } from "react-select/dist/declarations/src/filters.d.ts";
import {
  Form,
  type FormErrors,
  formValueSelector,
  getFormSyncErrors,
  type InjectedFormProps,
  reduxForm,
  SubmissionError,
} from "redux-form";
import { ActionButton, ActionButtonIconPlacement, ActionButtonIntent, ActionButtonSize } from "widgets";
import { usePrevious } from "widgets/hooks";

import { type IUploadFileResponse } from "@/api/WebApiClient.ts";
import {
  getFormError,
  getFormWarning,
  type ISelectOption,
  shouldAsyncValidateEvenIfPristineOrInitialized,
  simpleDropdownFieldValidation,
} from "@/app-utils/form.ts";
import { getBlastInputPageTips } from "@/app-utils/help.tsx";
import { isNotASCII, pluralizeWord } from "@/app-utils/string.ts";
import { type Dictionary } from "@/app-utils/type.ts";
import { type BlastIndex, type BlastJob, type InputJobInput, type Template, type ValidationError } from "@/graphql.ts";
import { FILE_VALIDATION_WAITING } from "@/redux/action-types.ts";
import {
  clearSequenceIdentifiers,
  type IBlastPrefilledValuesState,
  loadBlastIndexList,
  submitBlastJob,
  validateBlastInputSequence,
} from "@/redux/modules/blast.ts";
import {
  abortFileUpload,
  allFilesHaveStatus,
  anyFileHasStatus,
  getFileServerPath,
  getFilesToBeValidated,
  type IFileUpload,
  invalidateAllFiles,
  pendingUploadOrValidation,
  resetAllFileUploads,
  uploadFile,
} from "@/redux/modules/file-upload.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { BLAST_METHOD, FILE_STATUS, JOB_TYPES, SEQUENCE_TYPE } from "@/settings/settings.ts";

import InputField, { InputReduxFormField } from "../../core/redux-form/InputField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../../core/redux-form/ReactSelectField.js";
import TextareaDropzone, { TextareaDropzoneReduxFormField } from "../../core/redux-form/TextareaDropzone.js";
import TipTour from "../../core/TipTour.js";
import { FieldTitle } from "../FieldTitle.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";
import BlastAdvancedPanel from "./BlastAdvancedPanel.js";
import { BLAST_OPTIONS, type IBlastSelectOption, ParamValues } from "./BlastCommon.js";

export interface IBlastInputFormProps extends IPlaybookWizardStepProvidedProps {
  fileUploads: Dictionary<IFileUpload>;
}

const FORM_KEYS = ParamValues.FORM_KEYS;
export const BLAST_MAX_HITS = 9999;
export const BLAST_MAX_HSPS = 9999;
export const BLAST_MAX_EVALUE = 1000;

/**
 * BlastInputFormImpl is used in the context of the parent BlastPlaybookPage/BlastInputPage (which will load the blastIndexList prop) ;
 * here we have all required fields for submitting a BLAST job using redux-form together with a file upload mechanism.
 */
const BLAST_FORM_NAME = "BLAST_FORM";

interface BlastInputFormImplProps
  extends IBlastInputFormProps,
    IPlaybookWizardStepInjectedProps,
    InjectedFormProps<FormData, any> {}

const BlastInputFormImpl: FC<BlastInputFormImplProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();

  // initialization
  const initialJobForRerun = useSelector<IAppState, BlastJob>((state) => state.job.initialJobForRerun);
  const blastPrefilledValues = useSelector<IAppState, IBlastPrefilledValuesState>(
    (state) => state.blast.prefilledValues,
  );
  useEffect(() => {
    const initialValues: any = ParamValues.getInitialBLASTParams(initialJobForRerun, blastPrefilledValues);
    props.initialize(initialValues);
    const hasInitialSequence = !!initialValues?.[FORM_KEYS.BLAST_INPUT_FORM_SEQUENCE_FIELD];
    if (hasInitialSequence) {
      props.touch(FORM_KEYS.BLAST_INPUT_FORM_SEQUENCE_FIELD); // makes sure the error is shown (if any)
      setTimeout(props.asyncValidate, 200);
    }
  }, [initialJobForRerun, blastPrefilledValues, props.asyncValidate]);

  const {
    blastMethod,
    currentMatchMismatchScores,
    currentMatrix,
    currentGapCosts,
    currentWordSize,
    currentGeneticCode,
  } = useSelector<IAppState, any>((state) => {
    const valueSelector = formValueSelector(BLAST_FORM_NAME);
    return {
      blastMethod: valueSelector(state, FORM_KEYS.BLAST_INPUT_FORM_BLAST_METHOD)?.value,
      currentMatchMismatchScores: valueSelector(state, FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MATCH_MISMATCH_SCORE_FIELD),
      currentMatrix: valueSelector(state, FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MATRIX_FIELD),
      currentGapCosts: valueSelector(state, FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_GAP_COST_FIELD),
      currentWordSize: valueSelector(state, FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_WORD_SIZE_FIELD),
      currentGeneticCode: valueSelector(state, FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_GENETIC_CODE_FIELD),
    };
  });

  const blockAdvancedPanel = useSelector<IAppState, FormErrors>((state) => {
    const formErrors = getFormSyncErrors(BLAST_FORM_NAME)(state);
    return (
      formErrors[FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_EXPECT_THRESHOLD_FIELD] ||
      formErrors[FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MAX_MATCHES_FIELD]
    );
  });

  const globalFormError = useSelector<IAppState, string>((state) => getFormError(BLAST_FORM_NAME, state));
  const globalFormWarning = useSelector<IAppState, string>((state) => getFormWarning(BLAST_FORM_NAME, state));

  const [openAdvancedParameter, setOpenAdvancedParameter] = useState<boolean>(false);

  useEffect(() => {
    // state cleanup
    return () => {
      dispatch(clearSequenceIdentifiers);
    };
  }, []);

  useEffect(() => {
    // if the blast method is modified then we need to validate again all files
    dispatch(invalidateAllFiles());
  }, [blastMethod]);

  const previousFileUploads = usePrevious(props.fileUploads);
  useEffect(() => {
    if (
      anyFileHasStatus(props.fileUploads, FILE_STATUS.VALIDATION_WAITING) ||
      Object.keys(previousFileUploads || {}).length !== Object.keys(props.fileUploads).length
    ) {
      props.asyncValidate();
    }
  }, [props.fileUploads]);

  const handleSubmitData = useCallback(
    (values, _dispatch, props: BlastInputFormImplProps): Promise<BlastJob> => {
      const jobInput = createBlastJobSubmissionInput(values, props.fileUploads);
      return dispatch(submitBlastJob(jobInput)).then(
        (fulfilled) => {
          props.proceedWithNextStep();
          return fulfilled;
        },
        // do nothing, the original "submitBlastJob" already executed the business logic regarding the success case
        // the following will throw an error that redux-form understands as a submission error, with a general error that will be
        // available from this.props.error
        (rejected) => {
          throw new SubmissionError({ _error: rejected.message });
        },
      );
    },
    [props.fileUploads],
  );

  const filterBlastIndicesOptions = useCallback(
    (option: FilterOptionOption<any>, _rawInput: string) => {
      const blastIndex: BlastIndex = option.data;
      switch (blastMethod.toLowerCase()) {
        case BLAST_METHOD.BLASTN:
        case BLAST_METHOD.TBLASTN:
          return blastIndex.sequenceType !== SEQUENCE_TYPE.PROTEIN;
        case BLAST_METHOD.BLASTP:
        case BLAST_METHOD.BLASTX:
          return blastIndex.sequenceType === SEQUENCE_TYPE.PROTEIN;
        default:
          return true;
      }
    },
    [blastMethod],
  );

  const advancedPanelClassName: string = clsx(" form-column blast-job-advanced-params", {
    "blast-job-advanced-params-open": openAdvancedParameter,
  });

  // directly after the upload we set the status of the file to "validation waiting" ; if upstream validation failed then this will stay in the status until the previous validation is resolved
  const uploadCallback = useCallback((file: File, formData: FormData) => {
    dispatch(uploadFile(file, formData)).then((response: IUploadFileResponse) => {
      if (response) {
        // only in case of success
        dispatch({ type: FILE_VALIDATION_WAITING, payload: [response] });
      }
    });
  }, []);

  if (!props.initialized) {
    return null;
  }

  return (
    <PlaybookWizardStep
      {...props}
      playbookPage={{ ...props.playbookPage }}
      wizardFooterData={{
        error: Array.isArray(globalFormError) ? globalFormError?.[0] : globalFormError || props.error,
        warning: globalFormWarning,
        nextButtonProps: {
          label: "Submit",
          isLoading: !!(props.asyncValidating || props.submitting || pendingUploadOrValidation(props.fileUploads)),
          onPress: props.handleSubmit(handleSubmitData),
        },
      }}
    >
      <TipTour tips={getBlastInputPageTips()}>
        <div className="fill">
          <div className={"blast-input-page blast-input fill"}>
            <div className={"blast-input-form "}>
              <Form className="form-column" onSubmit={props.handleSubmit(handleSubmitData)}>
                <ReactSelectReduxFormField
                  name={FORM_KEYS.BLAST_INPUT_FORM_BLAST_METHOD}
                  label={"BLAST method"}
                  component={ReactSelectField}
                  options={BLAST_OPTIONS}
                  isSearchable={false}
                  isClearable={false}
                  formatOptionLabel={blastOptionFormatter}
                  validate={simpleDropdownFieldValidation}
                />

                <TextareaDropzoneReduxFormField
                  name={FORM_KEYS.BLAST_INPUT_FORM_SEQUENCE_FIELD}
                  title={`${blastMethod?.toUpperCase()} query sequences`}
                  placeholder="Enter FASTA sequence(s) or sequence identifiers or drop files here"
                  component={TextareaDropzone}
                  horizontalLayout={false}
                  validate={validateSequenceField}
                  handleUploadFile={uploadCallback}
                  abortFileupload={(fileName: string) => dispatch(abortFileUpload(fileName))}
                  fileUploads={props.fileUploads}
                  hotspotId={"blast-form-sequence-input"}
                />

                <ReactSelectReduxFormField
                  name={FORM_KEYS.BLAST_INPUT_FORM_BLAST_INDEX_FIELD}
                  label="Databases"
                  placeholder="Type to search databases..."
                  component={ReactSelectField}
                  isMulti={true}
                  horizontalLayout={false}
                  closeMenuOnSelect={false}
                  formatOptionLabel={blastIndexOptionFormatter}
                  hotspotId={"blast-form-database-input"}
                  async={true}
                  loadOptions={(searchTerm) => dispatch(loadBlastIndexList(searchTerm))}
                  filterOption={filterBlastIndicesOptions}
                  noOptionsMessage={() => "No matching database..."}
                  getOptionValue={getUniqueBlastIdentifier}
                  isSearchable
                  defaultOptions={true}
                  cacheOptions={true}
                  validate={validateBlastIndexField}
                />

                <InputReduxFormField
                  horizontalLayout={false}
                  componentClass="input"
                  key={"event-name"}
                  name={FORM_KEYS.BLAST_INPUT_FORM_EVENT_NAME_FIELD}
                  label={<FieldTitle label={"Event name"} />}
                  component={InputField}
                  placeholder={selMessage({ id: "form.input.placeholder.optional" })}
                  maxLength={DB_FIELD_MAX_LENGTH.EVENT.NAME}
                  tooltip={"You can provide a name for the playbook event"}
                />
                <InputReduxFormField
                  horizontalLayout={false}
                  componentClass="input"
                  key={"event-description"}
                  name={FORM_KEYS.BLAST_INPUT_FORM_EVENT_DESCRIPTION_FIELD}
                  label={<FieldTitle label={"Event description"} />}
                  component={InputField}
                  placeholder="Enter text..."
                  maxLength={DB_FIELD_MAX_LENGTH.EVENT.DESCRIPTION}
                  tooltip={"You can provide a description for the playbook event"}
                />

                <div
                  className={clsx("advanced-params-switch", {
                    disabled: blockAdvancedPanel,
                    open: openAdvancedParameter,
                  })}
                >
                  <span id={"blast-form-advanced-params"} className={"hotspot-beacon"} />
                  <ActionButton
                    icon={!openAdvancedParameter ? "keyboard_double_arrow_down" : "keyboard_double_arrow_up"}
                    label={`${!openAdvancedParameter ? "Show" : "Hide"} Advanced Parameters`}
                    onPress={() => {
                      !blockAdvancedPanel && setOpenAdvancedParameter(!openAdvancedParameter);
                    }}
                    size={ActionButtonSize.Small}
                    iconPlacement={ActionButtonIconPlacement.Right}
                    intent={ActionButtonIntent.Outlined}
                  />
                </div>
              </Form>

              <div className={advancedPanelClassName}>
                <BlastAdvancedPanel
                  blastMethod={blastMethod}
                  currentMatchMismatchScore={currentMatchMismatchScores}
                  currentMatrix={currentMatrix}
                  currentGapCosts={currentGapCosts}
                  currentWordSize={currentWordSize}
                  currentGeneticCode={currentGeneticCode}
                  handleChangeState={props.change}
                />
              </div>
            </div>
          </div>
        </div>
      </TipTour>
    </PlaybookWizardStep>
  );
};

const validateSequenceField = (value) => {
  // also make sure that we are not validating when files have been dropped
  if ((Array.isArray(value) && !value.length) || (!Array.isArray(value) && !value?.trim().length)) {
    return "Sequence or sequence identifier or file is required.";
  }
  // If string contains non-ascii chars or unprintable chars from ascii (besides space, \n, \r, \t)
  if (!Array.isArray(value) && !!value && value.trim().length > 0 && isNotASCII(value.trim())) {
    return "Sequence text must not use characters outside ASCII character set.";
  }
};

const validateBlastIndexField = (blastIndexValue, allValues?: any) => {
  if (!blastIndexValue || blastIndexValue.length === 0) {
    return "Database or genome target is required.";
  } else {
    const blastMethod: string = allValues[FORM_KEYS.BLAST_INPUT_FORM_BLAST_METHOD]?.value;
    const isBlastWithNucleotideTarget: boolean =
      blastMethod === BLAST_METHOD.BLASTN || blastMethod === BLAST_METHOD.TBLASTN;
    const filterFnc: (type: string) => boolean = isBlastWithNucleotideTarget
      ? (type: string) => type === "protein"
      : (type: string) => type !== "protein";
    const invalidIndices: BlastIndex[] = (blastIndexValue as BlastIndex[]).filter((idx) => filterFnc(idx.sequenceType));
    if (invalidIndices.length > 0) {
      return `Found  ${pluralizeWord(
        "target",
        invalidIndices.length,
      )} with invalid sequence type for ${blastMethod.toUpperCase()}: ${invalidIndices
        .map((idx) => idx.name + " - " + idx.secondaryName + " (" + idx.sequenceType.toUpperCase() + ")")
        .join(", ")}`;
    }
  }
};

const getUniqueBlastIdentifier = (blastIndex: BlastIndex) => {
  // we need the id and label to be unique, so that react-select filters out automatically selected options
  // we might have colliding identifiers, since the blast indices are spawn from different DB tables !
  return `${blastIndex.name} ${blastIndex.secondaryName} ${blastIndex.sequenceType} `;
};

const blastOptionFormatter = (option: IBlastSelectOption): ReactElement => {
  return (
    <span className={"blast-option"}>
      <span>{option.label}</span>
      <span className="blast-option-description">&nbsp;&nbsp;&nbsp; {option.description}</span>
    </span>
  );
};

const blastIndexOptionFormatter = (option: BlastIndex, labelMeta: FormatOptionLabelMeta<Template>): ReactElement => {
  const isSelected: boolean = labelMeta.context !== "menu";
  const valueClass: string = clsx("blast-index-option", { "blast-selection": isSelected });

  return (
    <span className={valueClass}>
      <span className="main-label">
        {option.name} - {option.secondaryName}
      </span>
      <span className="sequence-type"> ({option.sequenceType})</span>
    </span>
  );
};

const createBlastJobSubmissionInput = (values, fileUploads: Dictionary<IFileUpload>) => {
  // check that all dropped files have been uploaded and validated successfully
  const hasDroppedFiles: boolean = hasBlastFiles(fileUploads);

  if (hasDroppedFiles) {
    if (!allFilesHaveStatus(fileUploads, FILE_STATUS.VALIDATION_COMPLETE)) {
      throw new SubmissionError({ _error: "All files should first be uploaded and validated successfully" });
    }
  }

  // workaround: handover targets as JSON strings instead of real data structures
  //  because current graphql cannot handle objects in arrays as input. // TODO not true any longer
  const indexes: string[] = values[FORM_KEYS.BLAST_INPUT_FORM_BLAST_INDEX_FIELD].map((v: any) => {
    const temp = { ...v };
    return JSON.stringify(temp).replace(/"/g, '\\"');
  });

  // The 'Number of hits' and the 'Number of HSPS' must fit into 4 digits ( c <= 9999 ).
  if (values[FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MAX_MATCHES_FIELD] > BLAST_MAX_HITS) {
    throw new SubmissionError({
      _error: "The number of TARGET SEQUENCES has to be less than " + (BLAST_MAX_HITS + 1),
    });
  }
  if (values[FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MAX_TARGET_SEQ_FIELD] > BLAST_MAX_HSPS) {
    throw new SubmissionError({
      _error: "The number of MATCHES IN A QUERY RANGE has to be less than " + (BLAST_MAX_HSPS + 1),
    });
  }

  const matchMismatchScores: ISelectOption = values[FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MATCH_MISMATCH_SCORE_FIELD];
  const matrix: ISelectOption = values[FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MATRIX_FIELD];
  const compositionalAdjustments: ISelectOption =
    values[FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_COMPOSITIONAL_ADJUSTMENTS_FIELD];
  const maxTargetSeqs: ISelectOption = values[FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MAX_TARGET_SEQ_FIELD];
  const wordSize: ISelectOption = values[FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_WORD_SIZE_FIELD];
  const gapCost: ISelectOption = values[FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_GAP_COST_FIELD];
  const genCode: ISelectOption = values[FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_GENETIC_CODE_FIELD];

  const jobInput: InputJobInput = {
    alias: values[FORM_KEYS.BLAST_INPUT_FORM_EVENT_NAME_FIELD],
    note: values[FORM_KEYS.BLAST_INPUT_FORM_EVENT_DESCRIPTION_FIELD],
    type: JOB_TYPES.BLAST,
    params: {
      blast: {
        blastType: values[FORM_KEYS.BLAST_INPUT_FORM_BLAST_METHOD].value,
        sequenceQuery: hasDroppedFiles ? null : values[FORM_KEYS.BLAST_INPUT_FORM_SEQUENCE_FIELD],
        indexes: indexes,
        targets: null,
        files: hasDroppedFiles ? getFileServerPath(fileUploads).map((ob) => ob.filePath) : null,
        advancedOptions: {
          compositionalAdjustments: compositionalAdjustments?.value,
          expectThreshold: values[FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_EXPECT_THRESHOLD_FIELD],
          filterLowComplexity: values[FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_FILTER_LOW_COMPLEXITY_FIELD],
          gapCost: gapCost?.value,
          maskTable: values[FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MASK_TABLE_FIELD],
          maskLower: values[FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MASK_LOWER_FIELD],
          matchMismatchScores: matchMismatchScores?.value,
          matrix: matrix?.value,
          maxMatches: values[FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MAX_MATCHES_FIELD],
          maxTargetSeqs: maxTargetSeqs?.value,
          shortQueries: values[FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_SHORT_QUERY_FIELD],
          wordSize: wordSize?.value,
          genCode: genCode?.value,
        },
      },
    },
  };
  return jobInput;
};

// currently only the sequence field is validated
const asyncValidate = async (values: any, dispatch: any, props: IBlastInputFormProps) => {
  if (values == null) {
    return;
  }
  try {
    const hasDroppedFiles: boolean = hasBlastFiles(props.fileUploads);
    const filesToBeValidated: IFileUpload[] = getFilesToBeValidated(props.fileUploads);
    // file specific checks
    if (hasDroppedFiles) {
      // if an upload has failed then there is no need to continue validation
      // Testing Hint: Mimic a failed upload by removing the authorization header in method uploadFile of WebApiClient.ts
      if (anyFileHasStatus(props.fileUploads, FILE_STATUS.UPLOAD_FAILED)) {
        throw "One or more files failed to upload";
      }
      if (anyFileHasStatus(props.fileUploads, FILE_STATUS.VALIDATION_FAILED)) {
        throw "One or more files failed the validation";
      }

      // if all files have been validated already then we send a no-error response
      if (filesToBeValidated.length === 0) {
        return;
      }
    }
    const validationErrors: ValidationError[] = await dispatch(
      validateBlastInputSequence(
        values[FORM_KEYS.BLAST_INPUT_FORM_BLAST_METHOD],
        hasDroppedFiles ? null : values[FORM_KEYS.BLAST_INPUT_FORM_SEQUENCE_FIELD],
        hasDroppedFiles ? filesToBeValidated : null,
      ),
    );

    // show error callout below dropzone only if there is precisely one error, e.g. fine contains too many sequences
    // see SEL9-546
    if (validationErrors?.length >= 1) {
      throw validationErrors.map((ve: ValidationError, i: number) => <div key={ve.message + i}>{ve.message}</div>);
    }
  } catch (error) {
    throw {
      [FORM_KEYS.BLAST_INPUT_FORM_SEQUENCE_FIELD]:
        error instanceof Error || (error.message && typeof error.message === "string")
          ? "Could not validate the input: " + error.message
          : error || "An unexpected error occurred",
    };
  }

  return;
};

const hasBlastFiles = (blastFileUploads: Dictionary<IFileUpload>) => {
  return !!Object.keys(blastFileUploads).length;
};

const BlastInputForm = reduxForm({
  form: BLAST_FORM_NAME,
  asyncValidate: asyncValidate,
  shouldAsyncValidate: shouldAsyncValidateEvenIfPristineOrInitialized,
  asyncBlurFields: [FORM_KEYS.BLAST_INPUT_FORM_SEQUENCE_FIELD], // so that only blurring this field triggers the async validation
})(BlastInputFormImpl) as any;

const BlastSequenceFileUploadManager: FC<Omit<IBlastInputFormProps, "fileUploads">> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const fileUploads = useSelector<IAppState, Dictionary<IFileUpload>>(
    (state) => state.filesUploadState.filesUploadLookup,
  );

  // state cleanup
  useEffect(() => {
    return () => {
      dispatch(resetAllFileUploads());
    };
  }, []);

  // so that the redux-form async validation has access to fileUploads via the props !
  return <BlastInputForm {...props} fileUploads={fileUploads} />;
};

export { BlastSequenceFileUploadManager as BlastInputForm };
